import { teacherProfileAtom } from '@openeducation/oe-it-jotai-atoms';
import { NotificationService } from '@openeducation/oe-it-services';
import type {
  NotificationType,
  ServerNotification,
} from '@openeducation/oe-it-shared-types';
import { useAtomValue } from 'jotai';
import { useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { useEnvironmentContext } from '../environment/environment.provider';

const FIVE_SECONDS_DELAY_MS = 5000;
const ONE_MINUTE_DELAY_MS = 60000;

interface GenericNotificationsArgs {
  queryKey: string;
  notificationType?: NotificationType;
  isEnabled?: boolean;
}

export function useGenericNotifications<T extends ServerNotification>({
  queryKey,
  notificationType,
  isEnabled = true,
}: GenericNotificationsArgs) {
  const queryClient = useQueryClient();
  const { environment } = useEnvironmentContext();
  const teacherProfile = useAtomValue(teacherProfileAtom);

  const timerRef = useRef<NodeJS.Timeout | undefined>();

  return useQuery<T[]>(
    [queryKey, teacherProfile?.id],
    () =>
      NotificationService.getNotifications(
        environment,
        teacherProfile?.id,
        notificationType
      ),
    {
      enabled: !!teacherProfile?.id && isEnabled,
      refetchInterval: ONE_MINUTE_DELAY_MS,
      onSuccess: (notifications) => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }

        const lowestExpirationMs = notifications?.reduce(
          (lowest, n) => Math.min(lowest, n.expirationMs || 0),
          -1
        );

        if (lowestExpirationMs > 0) {
          timerRef.current = setTimeout(() => {
            queryClient.invalidateQueries([queryKey, teacherProfile?.id]);
          }, lowestExpirationMs + FIVE_SECONDS_DELAY_MS);
        }
      },
    }
  );
}
